import type { Document as RichTextDocument } from '@contentful/rich-text-types';
import Grid from '@mui/material/GridLegacy';
// biome-ignore lint/correctness/noUnusedImports: Automated migration to Biome
import React from 'react';
import { setImgWidth } from '@/utils/frontify';
import type {
  Contentful,
  FrontifyImageContent,
} from '../../contentful/contentful-exports';
import useDeviceWidth from '../../hooks/deviceWidth';
import CTA, {
  type CTAContent,
  type FieldlessCTAContent,
} from '../Elements/CTA';
import Button, { type ButtonContent } from '../OldButton/OldButton';
import RichText from '../RichText/RichText';
import useStyles from './ImageGrid3ColWithDescription.style';

interface ColContentSectionProps {
  image: string;
  title?: string;
  description?: RichTextDocument;
  button?: ButtonContent | CTAContent;
  isVertical: boolean;
}

/**
 * @deprecated V2 component
 */

// biome-ignore lint/correctness/noUnusedVariables: Automated migration to Biome
interface Props {
  content: ImageGrid3ColWithDescriptionContent;
}
function ColContentSection({
  image,
  title,
  description,
  button,
  isVertical,
}: ColContentSectionProps) {
  const { classes } = useStyles();
  const lgSectionColumns = isVertical ? 12 : 4;

  return (
    <Grid
      item
      className={
        isVertical ? classes.contentSectionVertical : classes.contentSection
      }
      lg={lgSectionColumns}
      xs={12}
    >
      {/** biome-ignore lint/performance/noImgElement: Automated migration to Biome */}
      <img
        src={image}
        className={isVertical ? classes.imageVertical : classes.image}
        alt={title}
      />
      <div
        className={
          isVertical ? classes.colTextContentVertical : classes.colTextContent
        }
      >
        {title && <p className={classes.columnTitle}>{title}</p>}
        {description && (
          <div className={classes.columnDescription}>
            <RichText content={description} title={''} />
          </div>
        )}
        {button && (
          <div className={classes.button}>
            {button.sys.contentType.sys.id === 'button' ? (
              <Button {...button.fields} />
            ) : (
              <CTA content={button.fields as FieldlessCTAContent} />
            )}
          </div>
        )}
      </div>
    </Grid>
  );
}

export interface ImageGrid3ColWithDescriptionContent extends Contentful {
  fields: {
    orientation?: 'Vertical' | 'Horizontal';
    title: string;
    subtitle: string;
    column1Image: FrontifyImageContent[];
    column1Title?: string;
    column1Description?: RichTextDocument;
    column1Button?: ButtonContent | CTAContent;
    column2Image: FrontifyImageContent[];
    column2Title?: string;
    column2Description?: RichTextDocument;
    column2Button?: ButtonContent | CTAContent;
    column3Image: FrontifyImageContent[];
    column3Title?: string;
    column3Description?: RichTextDocument;
    column3Button: ButtonContent | CTAContent;
    altText1?: string;
    altText2?: string;
    altText3?: string;
  };
  metaData: {
    tags: [];
  };
}

interface Props {
  content: ImageGrid3ColWithDescriptionContent;
}

// biome-ignore lint/suspicious/noRedeclare: Automated migration to Biome
// biome-ignore lint/correctness/noUnusedVariables: Automated migration to Biome
interface RichText {
  value: string;
}

export default function ImageGrid3ColWithDescription({ content }: Props) {
  const { classes } = useStyles();

  const {
    orientation = 'Horizontal',
    title,
    subtitle,
    column1Image,
    column1Title,
    column1Description,
    column1Button,
    column2Image,
    column2Title,
    column2Description,
    column2Button,
    column3Image,
    column3Title,
    column3Description,
    column3Button,
    altText1,
    altText2,
    altText3,
  } = content.fields;
  const image1 = `${setImgWidth(column1Image?.[0].preview_url, 650)}`;
  const image2 = `${setImgWidth(column2Image?.[0].preview_url, 650)}`;
  const image3 = `${setImgWidth(column3Image?.[0].preview_url, 650)}`;
  const { isMobile } = useDeviceWidth();

  const isVertical = orientation === 'Vertical' || isMobile;

  if (isVertical) {
    return (
      <div className={classes.contentContainer}>
        <Grid container className={classes.container}>
          <Grid item className={classes.titleContainer} lg={12} xs={12}>
            <h2 className={classes.title}>{title}</h2>
            {subtitle && <div className={classes.subtitle}>{subtitle}</div>}
          </Grid>
          <div
            className={
              isVertical
                ? classes.colContentContainerVertical
                : classes.colContentContainer
            }
          >
            <ColContentSection
              title={column1Title}
              description={column1Description}
              image={image1}
              button={column1Button}
              isVertical={isVertical}
            />
            <ColContentSection
              title={column2Title}
              description={column2Description}
              image={image2}
              button={column2Button}
              isVertical={isVertical}
            />
            <ColContentSection
              title={column3Title}
              description={column3Description}
              image={image3}
              button={column3Button}
              isVertical={isVertical}
            />
          </div>
        </Grid>
      </div>
    );
  } else {
    return (
      <div className={classes.contentContainer}>
        <Grid container className={classes.container}>
          <Grid item className={classes.titleContainer} lg={12} xs={12}>
            <h2 className={classes.title}>{title}</h2>
            {subtitle && <div className={classes.subtitle}>{subtitle}</div>}
            {/* Images row */}
            <div className={classes.horizontalRow}>
              <div className={classes.horizontalColumn}>
                {/** biome-ignore lint/performance/noImgElement: Automated migration to Biome */}
                <img src={image1} className={classes.image} alt={altText1} />
              </div>
              <div className={classes.horizontalCentralColumn}>
                {/** biome-ignore lint/performance/noImgElement: Automated migration to Biome */}
                <img src={image2} className={classes.image} alt={altText2} />
              </div>
              <div className={classes.horizontalColumn}>
                {/** biome-ignore lint/performance/noImgElement: Automated migration to Biome */}
                <img src={image3} className={classes.image} alt={altText3} />
              </div>
            </div>
            {/* Title row */}
            <div className={classes.horizontalRow}>
              <div className={classes.horizontalColumn}>
                {column1Title && (
                  <p className={classes.columnTitle}>{column1Title}</p>
                )}
              </div>
              <div className={classes.horizontalCentralColumn}>
                {column2Title && (
                  <p className={classes.columnTitle}>{column2Title}</p>
                )}
              </div>
              <div className={classes.horizontalColumn}>
                {column3Title && (
                  <p className={classes.columnTitle}>{column3Title}</p>
                )}
              </div>
            </div>
            {/* Description row */}
            <div className={classes.horizontalRow}>
              <div className={classes.horizontalColumn}>
                {column1Description && (
                  <div className={classes.columnDescription}>
                    <RichText content={column1Description} title={''} />
                  </div>
                )}
              </div>
              <div className={classes.horizontalCentralColumn}>
                {column2Description && (
                  <div className={classes.columnDescription}>
                    <RichText content={column2Description} title={''} />
                  </div>
                )}
              </div>
              <div className={classes.horizontalColumn}>
                {column3Description && (
                  <div className={classes.columnDescription}>
                    <RichText content={column3Description} title={''} />
                  </div>
                )}
              </div>
            </div>
            {/* Button row */}
            <div className={classes.horizontalRow}>
              <div className={classes.horizontalColumn}>
                {column1Button && (
                  <div className={classes.button}>
                    {column1Button.sys.contentType.sys.id === 'button' ? (
                      <Button {...column1Button.fields} />
                    ) : (
                      <CTA
                        content={column1Button.fields as FieldlessCTAContent}
                      />
                    )}
                  </div>
                )}
              </div>
              <div className={classes.horizontalCentralColumn}>
                {column2Button && (
                  <div className={classes.button}>
                    {column2Button.sys.contentType.sys.id === 'button' ? (
                      <Button {...column2Button.fields} />
                    ) : (
                      <CTA
                        content={column2Button.fields as FieldlessCTAContent}
                      />
                    )}
                  </div>
                )}
              </div>
              <div className={classes.horizontalColumn}>
                {column3Button && (
                  <div className={classes.button}>
                    {column3Button.sys.contentType.sys.id === 'button' ? (
                      <Button {...column3Button.fields} />
                    ) : (
                      <CTA
                        content={column3Button.fields as FieldlessCTAContent}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </Grid>
        </Grid>
      </div>
    );
  }
}
